import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  customersService,
  type CustomerCreatePayload,
  type AddressCreatePayload,
} from '@/services/customers';
import { useTenantLocale } from '@/hooks/useTenantLocale';
import type { CustomerListRow, CustomerStatus } from '@/types/customers';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set on edit, null on create. */
  customer: CustomerListRow | null;
}

const EMPTY: CustomerCreatePayload = {
  name: '',
  code: '',
  email: '',
  phone: '',
  gstin: '',
  externalRef: '',
  status: 'ACTIVE',
  defaultCurrency: '',
};

export function CustomerFormSheet({ open, onOpenChange, customer }: Props) {
  const qc = useQueryClient();
  const locale = useTenantLocale();
  const isEdit = !!customer;
  const [form, setForm] = useState<CustomerCreatePayload>(EMPTY);
  // Default-billing address — flows to the invoice Bill-To. Upserted on save.
  const [addr, setAddr] = useState({ line1: '', line2: '', city: '', state: '', postalCode: '' });
  const [billingAddrId, setBillingAddrId] = useState<string | null>(null);
  const EMPTY_ADDR = { line1: '', line2: '', city: '', state: '', postalCode: '' };

  useEffect(() => {
    if (!open) return;
    if (customer) {
      setForm({
        name: customer.name,
        code: customer.code,
        email: customer.email ?? '',
        phone: customer.phone ?? '',
        gstin: customer.gstin ?? '',
        externalRef: customer.externalRef ?? '',
        status: customer.status,
        defaultCurrency: customer.defaultCurrency ?? '',
      });
      let alive = true;
      customersService
        .listAddresses(customer.id)
        .then((addrs) => {
          if (!alive) return;
          const b = addrs.find((a) => a.isDefaultBilling) ?? addrs[0] ?? null;
          setBillingAddrId(b?.id ?? null);
          setAddr(
            b
              ? {
                  line1: b.line1 ?? '',
                  line2: b.line2 ?? '',
                  city: b.city ?? '',
                  state: b.state ?? '',
                  postalCode: b.postalCode ?? '',
                }
              : EMPTY_ADDR,
          );
        })
        .catch(() => undefined);
      return () => {
        alive = false;
      };
    } else {
      setForm(EMPTY);
      setBillingAddrId(null);
      setAddr(EMPTY_ADDR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: CustomerCreatePayload = {
        ...form,
        name: form.name.trim(),
        code: emptyToUndef(form.code),
        email: emptyToUndef(form.email),
        phone: emptyToUndef(form.phone),
        gstin: form.gstin?.trim() ? form.gstin.trim().toUpperCase() : undefined,
        externalRef: emptyToUndef(form.externalRef),
        defaultCurrency: form.defaultCurrency?.trim()
          ? form.defaultCurrency.trim().toUpperCase()
          : undefined,
      };
      let customerId: string;
      if (isEdit && customer) {
        const { code: _code, ...patch } = payload;
        await customersService.update(customer.id, patch);
        customerId = customer.id;
      } else {
        const created = await customersService.create(payload);
        customerId = created.id;
      }

      // Upsert the default-billing address (only when the essentials are filled).
      if (addr.line1.trim() && addr.city.trim() && addr.state.trim()) {
        const addrPayload: AddressCreatePayload = {
          line1: addr.line1.trim(),
          line2: addr.line2.trim() || undefined,
          city: addr.city.trim(),
          state: addr.state.trim(),
          postalCode: addr.postalCode.trim() || undefined,
          // Country is left to the backend (defaults to the tenant's ISO country);
          // sending a full name like "India" fails its 2-char ISO validation.
          gstin: payload.gstin || undefined,
          role: 'BOTH',
          isDefaultBilling: true,
        };
        try {
          if (billingAddrId) {
            await customersService.updateAddress(customerId, billingAddrId, addrPayload);
          } else {
            await customersService.createAddress(customerId, addrPayload);
          }
        } catch (err) {
          const msg = (err as { message?: string })?.message ?? 'unknown error';
          toast.warning(`Customer saved, but the billing address failed: ${msg}`);
        }
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Customer updated' : 'Customer created');
      qc.invalidateQueries({ queryKey: ['customers'] });
      onOpenChange(false);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    mutation.mutate();
  };

  const HeroIcon = isEdit ? Pencil : Plus;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      fixedHeight="lg"
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            {isEdit && customer ? (
              <span className="text-[14px] font-semibold">
                {initials(customer.name)}
              </span>
            ) : (
              <HeroIcon className="h-6 w-6" />
            )}
          </span>

          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>
              {isEdit ? `Edit customer · ${customer?.code}` : 'New customer'}
            </DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              {isEdit ? customer?.name : 'Create customer'}
            </DialogTitle>

            {isEdit && customer ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <Badge variant="muted" className="font-mono text-[10px]">
                  {customer.code}
                </Badge>
                {customer.gstin && (
                  <Badge variant="muted" className="font-mono text-[10px]">
                    {locale.labels.taxIdLabel}
                  </Badge>
                )}
                <Badge
                  variant={customer.status === 'ACTIVE' ? 'success' : 'muted'}
                  className="text-[10px]"
                >
                  {customer.status === 'ACTIVE' ? 'active' : 'archived'}
                </Badge>
                {customer._orderCount > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    · {customer._orderCount} order{customer._orderCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            ) : (
              <DialogDescription className="text-[12.5px] leading-relaxed">
                Add a customer record. They become available in the sales-order picker
                immediately.
              </DialogDescription>
            )}
          </div>
        </div>
      }
      title=""
      submitLabel={isEdit ? 'Save changes' : 'Create customer'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="3xl"
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Name" htmlFor="name" required>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Acme Industries Pvt Ltd"
            required
            autoComplete="off"
          />
        </FormField>
        <FormField
          label="Code"
          htmlFor="code"
          hint={isEdit ? 'Immutable' : 'Auto-generated when blank (CUST-XXXXXX)'}
        >
          <Input
            id="code"
            value={form.code ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="CUST-AB12CD"
            disabled={isEdit}
            autoComplete="off"
            className="font-mono"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="ops@acme.example"
            autoComplete="off"
          />
        </FormField>
        <FormField label="Phone" htmlFor="phone">
          <Input
            id="phone"
            value={form.phone ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+91 98765 43210"
            autoComplete="off"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          label={locale.labels.taxIdLabel}
          htmlFor="gstin"
          hint={
            locale.isIndia
              ? '15-char India GST number. Required for B2B GST invoices.'
              : `Tenant tax-ID equivalent for ${locale.country}. Optional.`
          }
        >
          <Input
            id="gstin"
            value={form.gstin ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
            placeholder={locale.isIndia ? '29AAAAA0000A1Z5' : ''}
            maxLength={locale.isIndia ? 15 : 32}
            autoComplete="off"
            className="font-mono"
          />
        </FormField>
        <FormField label="External reference" htmlFor="externalRef" hint="Customer ID in your CRM / portal.">
          <Input
            id="externalRef"
            value={form.externalRef ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, externalRef: e.target.value }))}
            placeholder="acme-prod-2310"
            autoComplete="off"
          />
        </FormField>
        <FormField
          label="Default currency"
          htmlFor="defaultCurrency"
          hint="Optional. ISO 4217 (e.g. USD, EUR). Orders for this customer default to this currency; leave blank to use the tenant base."
        >
          <Input
            id="defaultCurrency"
            value={form.defaultCurrency ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, defaultCurrency: e.target.value.toUpperCase() }))
            }
            placeholder="USD"
            maxLength={3}
            className="font-mono uppercase"
            autoComplete="off"
          />
        </FormField>
      </div>

      <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-3">
        <div className="text-[12px] font-semibold text-muted-foreground">
          Billing address
          <span className="ml-1.5 font-normal text-[11px]">
            — appears on this customer's tax invoices (Bill To).
          </span>
        </div>
        <FormField label="Road / Street" htmlFor="addrLine1">
          <Input
            id="addrLine1"
            value={addr.line1}
            onChange={(e) => setAddr((a) => ({ ...a, line1: e.target.value }))}
            placeholder="Sector-88, Gautam Budh Nagar"
            autoComplete="off"
          />
        </FormField>
        <FormField label="Area / Locality" htmlFor="addrLine2" hint="Optional.">
          <Input
            id="addrLine2"
            value={addr.line2}
            onChange={(e) => setAddr((a) => ({ ...a, line2: e.target.value }))}
            autoComplete="off"
          />
        </FormField>
        <div className="grid grid-cols-3 gap-4">
          <FormField label="City" htmlFor="addrCity">
            <Input
              id="addrCity"
              value={addr.city}
              onChange={(e) => setAddr((a) => ({ ...a, city: e.target.value }))}
              placeholder="Noida"
              autoComplete="off"
            />
          </FormField>
          <FormField label="State" htmlFor="addrState" hint="Full name — drives CGST/SGST vs IGST.">
            <Input
              id="addrState"
              value={addr.state}
              onChange={(e) => setAddr((a) => ({ ...a, state: e.target.value }))}
              placeholder="Uttar Pradesh"
              autoComplete="off"
            />
          </FormField>
          <FormField label="PIN" htmlFor="addrPin">
            <Input
              id="addrPin"
              value={addr.postalCode}
              onChange={(e) => setAddr((a) => ({ ...a, postalCode: e.target.value }))}
              placeholder="201305"
              autoComplete="off"
            />
          </FormField>
        </div>
      </div>

      {isEdit && (
        <FormField label="Status" htmlFor="status">
          <Select
            value={form.status ?? 'ACTIVE'}
            onValueChange={(v) => setForm((f) => ({ ...f, status: v as CustomerStatus }))}
          >
            <SelectTrigger id="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Archived</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      )}
    </FormSheet>
  );
}

function emptyToUndef(s: string | undefined | null): string | undefined {
  const t = s?.trim();
  return t ? t : undefined;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
